import { createAction } from 'redux-actions';
import { push } from 'react-router-redux';
import moment from 'moment';

import { wrapFetchFormData, wrapFetchAuth } from 'actions/endpoint';
import { openFailedAlert, closeAlert } from 'actions/alert';
import { pageChanging, pageChanged, loadingRatio } from 'actions/global';

import {
	addCreditCard,
	generateCreditData,
	clearValidateOrderCreditData,
	validateOrderCreditData,
} from 'actions/payment';

import {
	calculateMonth,
	ORDER_ITEM_EXTRA_PERSON_ID,
	ORDER_ITEM_PET_CLEANING_FEE_ID,
	ORDER_ITEM_PET_ID,
} from 'util/rent';
import { keyTransform, serverMapEmpty, isOutOfMonth, sleep } from 'util/helper';
import { t, languageMapRoute } from 'util/i18n';
import { examOrderPolicyData, orderPolicyKeyFrom, examCreditData, creditKeyFrom } from 'util/exam';
import AllCountries from 'util/country';

export const updateBookingField = createAction(
	'UPDATE_BOOKING_FIELD',
	(type, key, value) => (_, getState) => {
		if (type === 'creditForm') {
			if (['cardNumber', 'expireMonth', 'expireYear', 'cvc'].includes(key) && value.value !== '') {
				if (
					!/^\d+$/.test(value.value) ||
					(key === 'cardNumber' && value.value.length > 16) ||
					(key === 'expireMonth' && isOutOfMonth(value.value)) ||
					(key === 'expireYear' && value.value.length > 2) ||
					(key === 'cvc' && value.value.length > 3)
				) {
					const oldValue = getState().booking.creditForm[key].value;
					return {
						type,
						key,
						value: { value: oldValue },
					};
				}
			}
		}

		return {
			type,
			key,
			value,
		};
	},
);

export const updateBookingMonthEpic = action$ =>
	action$
		.ofType('UPDATE_BOOKING_FIELD')
		.filter(action => action.payload.type === 'form' && action.payload.key === 'dateFrom')
		.map(() => updateBookingField('form', 'rentMonth', { value: { range: [], yearRange: [] } }));

export const bookingGoToStep = createAction('BOOKING_GO_TO_STEP', step => ({ step }));

export const setBookingFormData = createAction(
	'SET_BOOKING_FORM_DATA',
	({
		address,
		emergencyContact,
		emergencyContactMobile,
		emergencyContactMobileCountryCode,
		emergencyContactRelationship,
		emergencyAddress,
	}) => ({
		address: { value: serverMapEmpty(address) },
		emergencyContact: {
			value: serverMapEmpty(emergencyContact),
		},
		emergencyContactMobile: {
			value: serverMapEmpty(emergencyContactMobile),
			country: AllCountries.getCountryByCode(emergencyContactMobileCountryCode),
		},
		emergencyContactRelationship: {
			value: serverMapEmpty(emergencyContactRelationship),
		},
		emergencyAddress: {
			value: serverMapEmpty(emergencyAddress),
		},
	}),
);

export const verifyStepOne = createAction('VERIFY_STEP_ONE', () => (dispatch, getState) => {
	const { booking: { form: { dateFrom, rentMonth } } } = getState();

	if (
		dateFrom.value === '' ||
		rentMonth.value.range.length === 0 ||
		rentMonth.value.yearRange.length === 0
	) {
		dispatch(openFailedAlert('booking', t('booking:notValidText')));
		return;
	}

	const { user: { data: userData } } = getState();

	dispatch(setBookingFormData(userData));

	dispatch(closeAlert('booking'));
	dispatch(bookingGoToStep(1));
});

export const uploadCertificateFile = createAction('UPLOAD_CERTIFICATE_FILE', (key, e) => {
	const files = e.target.files || e.dataTransfer.files;

	const file = files[0];

	if (typeof file === 'undefined') {
		return { filename: '', file: null };
	}

	return { filename: file.name, file, key };
});

export const clearCertificateFile = createAction('CLEAR_CERTIFICATE_FILE', key => ({ key }));

const generageOrderData = (lan, form, oldOrderData) => {
	const dateFrom = moment(form.dateFrom.value, 'X');

	const totalMonth = calculateMonth(
		[parseInt(dateFrom.format('M'), 10) - 1, ...form.rentMonth.value.range],
		[parseInt(dateFrom.format('YYYY'), 10), ...form.rentMonth.value.yearRange],
	);

	let orderItem = form.orderItem.value;

	// Filter the PET CLEANING FEE action if already pay before
	if (oldOrderData) {
		const oldOrderHasPet =
			oldOrderData.detail.filter(item => item.order_item_id === ORDER_ITEM_PET_ID).length > 0;

		if (oldOrderHasPet) {
			orderItem = orderItem.filter(id => id !== ORDER_ITEM_PET_CLEANING_FEE_ID);
		}
	}

	return {
		name: form.name.value,
		rent_month: totalMonth,
		date_from: dateFrom.format('YYYY/MM/DD'),
		date_to: dateFrom
			.add(totalMonth, 'M')
			.subtract(1, 'days')
			.format('YYYY/MM/DD'),
		address: form.address.value,
		certificate_type:
			form.certificateType.value.length > 0 ? form.certificateType.value[0].value : '',
		certificate_number: form.certificate.value,
		extra_certificate_type:
			form.extraCertificateType.value.length > 0 ? form.extraCertificateType.value[0].value : '',
		extra_certificate_number: form.extraCertificate.value,
		emergency_contact: form.emergencyContact.value,
		emergency_contact_mobile: form.emergencyContactMobile.value,
		emergency_contact_mobile_country_code: form.emergencyContactMobile.country.iso2,
		emergency_contact_relationship: form.emergencyContactRelationship.value,
		order_item_id: orderItem,
		certificate_file: form.certificateFile,
		extra_certificate_file: form.extraCertificateFile,
		emergency_address: form.emergencyAddress.value,
	};
};

const validateOrderPolicyData = createAction('VALIDATE_ORDER_POLICY_DATA', (key, valid) => ({
	key,
	valid,
}));

const clearValidateOrderPolicyData = createAction('CLEAR_VALIDATE_ORDER_POLICY_DATA');

export const verifyStepTwo = createAction('VERIFY_STEP_TWO', () => (dispatch, getState) => {
	const { i18n: { lan }, booking: { form } } = getState();

	const orderData = generageOrderData(lan, form);

	const orderCheckData = examOrderPolicyData(orderData);

	dispatch(clearValidateOrderPolicyData());

	if (!orderCheckData.value) {
		if (orderCheckData.notValid.includes('extra_certificate_number')) {
			dispatch(openFailedAlert('booking', t('booking:notValidCertificate')));
		} else {
			dispatch(openFailedAlert('booking', t('booking:notValidText')));
		}

		const notValidData = orderPolicyKeyFrom(orderCheckData.notValid);
		notValidData.forEach(key => dispatch(validateOrderPolicyData(key, false)));
		window.scrollTo(0, 0);
		return;
	}

	if (!form.emergencyContactMobile.validate) {
		dispatch(openFailedAlert('booking', t('booking:notValidFormat')));
		dispatch(validateOrderPolicyData('emergencyContactMobile', false));
		window.scrollTo(0, 0);
		return;
	}

	if (!form.accept.value) {
		dispatch(openFailedAlert('booking', t('booking:notAcceptText')));
		window.scrollTo(0, 0);
		return;
	}

	if (!form.acceptInsurance.value) {
		dispatch(openFailedAlert('booking', t('booking:notAcceptInsuranceText')));
		window.scrollTo(0, 0);
		return;
	}

	dispatch(closeAlert('booking'));
	dispatch(bookingGoToStep(2));
});

export const createOrder = createAction(
	'CREATE_ORDER',
	(roomId, orderId) => async (dispatch, getState) => {
		const { i18n: { lan }, booking: { creditForm } } = getState();

		const creditData = generateCreditData(creditForm);

		const creditCheckData = examCreditData(creditData);

		dispatch(clearValidateOrderCreditData());

		if (!creditCheckData.value) {
			if (creditCheckData.notValid.includes('card_number')) {
				dispatch(validateOrderCreditData('cardNumber', false));
				window.scrollTo(0, 0);
				throw openFailedAlert('booking', t('booking:notValidCreditNumber'));
			}

			if (
				creditCheckData.notValid.includes('expiry_year') ||
				creditCheckData.notValid.includes('expiry_month')
			) {
				dispatch(validateOrderCreditData('expireYear', false));
				dispatch(validateOrderCreditData('expireMonth', false));
				window.scrollTo(0, 0);
				throw openFailedAlert('booking', t('booking:notValidCreditDate'));
			}

			if (creditCheckData.notValid.includes('security_code')) {
				dispatch(validateOrderCreditData('cvc', false));
				window.scrollTo(0, 0);
				throw openFailedAlert('booking', t('booking:notValidCreditCVC'));
			}

			const notValidData = creditKeyFrom(creditCheckData.notValid);
			notValidData.forEach(key => dispatch(validateOrderCreditData(key, false)));
			window.scrollTo(0, 0);
			throw openFailedAlert('booking', t('booking:notValidCreditNumber'));
		}

		const {
			booking: { form, data: bookingData },
			user: { data: { name, certificateType, certificateNumber } },
		} = getState();

		const oldOrderData = orderId ? bookingData[orderId] : null;

		const orderData = generageOrderData(lan, form, oldOrderData);

		const orderFormData = new FormData();

		orderFormData.append('room_id', roomId);
		orderFormData.append('name', name);
		orderFormData.append('rent_month', orderData.rent_month);
		orderFormData.append('date_from', orderData.date_from);
		orderFormData.append('date_to', orderData.date_to);
		orderFormData.append('address', orderData.address);
		orderFormData.append('certificate_type', certificateType);
		orderFormData.append(
			'certificate_file',
			orderData.certificate_file.file,
			orderData.certificate_file.filename,
		);
		orderFormData.append('certificate_number', certificateNumber);
		orderFormData.append('emergency_contact', orderData.emergency_contact);
		orderFormData.append('emergency_contact_mobile', orderData.emergency_contact_mobile);
		orderFormData.append(
			'emergency_contact_mobile_country_code',
			orderData.emergency_contact_mobile_country_code,
		);
		orderFormData.append(
			'emergency_contact_relationship',
			orderData.emergency_contact_relationship,
		);
		orderFormData.append('emergency_address', orderData.emergency_address);

		orderData.order_item_id.forEach(id => orderFormData.append('order_item_id[]', id));

		// Have 第二人入住
		if (form.orderItem.value.includes(ORDER_ITEM_EXTRA_PERSON_ID)) {
			orderFormData.append('extra_certificate_type', orderData.extra_certificate_type);
			orderFormData.append(
				'extra_certificate_file',
				orderData.extra_certificate_file.file,
				orderData.extra_certificate_file.filename,
			);
			orderFormData.append('extra_certificate_number', orderData.extra_certificate_number);
		}

		if (orderId) {
			orderFormData.append('original_order_id', orderId);
		}

		// Credit Card Data
		orderFormData.append('card_number', creditData.card_number);
		orderFormData.append('expiry_year', creditData.expiry_year);
		orderFormData.append('expiry_month', creditData.expiry_month);
		orderFormData.append('security_code', creditData.security_code);
		orderFormData.append('credit_name', creditData.credit_name);

		dispatch(pageChanging('CREATE_BOOKING_ORDER'));
		dispatch(loadingRatio(0.3, 'CREATE_BOOKING_ORDER'));

		const { status, message } = await wrapFetchFormData('order', {
			method: 'POST',
			body: orderFormData,
		});

		dispatch(loadingRatio(0.8, 'CREATE_BOOKING_ORDER'));
		await sleep(300);
		dispatch(pageChanged('CREATE_BOOKING_ORDER'));

		if (status !== 200 && status !== 201) {
			if (message === 'credit card number error') {
				dispatch(openFailedAlert('booking', t('booking:notValidCreditNumber')));
				return;
			}

			if (message === 'credit card expired') {
				dispatch(openFailedAlert('booking', t('booking:expiredCreditCard')));
				return;
			}

			dispatch(openFailedAlert('booking', message));
			return;
		}

		dispatch(closeAlert('booking'));
		dispatch(bookingGoToStep(3));
	},
);

export const editPay = createAction('CONFIRM_PAY', () => async (dispatch, getState) => {
	await dispatch(addCreditCard());

	const { i18n: { lan } } = getState();

	dispatch(push(`/${languageMapRoute(lan)}/user/booking`));
});

export const getOrder = createAction('FETCH_ORDER', async () => {
	const { status, data, message } = await wrapFetchAuth('order', { method: 'GET' });

	if (status !== 200) {
		throw openFailedAlert('generic', message);
	}

	const bookingData = {};
	const list = data.map(item => {
		bookingData[item.id] = keyTransform(item);

		return item.id;
	});

	const current = list.filter(id => ['REVIEW', 'PROCESSING'].includes(bookingData[id].status));
	const old = list.filter(id => ['FINISHED', 'CANCEL'].includes(bookingData[id].status));

	return { current, old, data: bookingData };
});

export const getOrderById = createAction('FETCH_ORDER_BY_ID', async orderId => {
	const { status, data, message } = await wrapFetchAuth(`order/${orderId}`, { method: 'GET' });

	if (status !== 200) {
		throw openFailedAlert('generic', message);
	}

	return { data: keyTransform(data) };
});

export const getOrderItemByRoomId = createAction('FETCH_ORDER_ITEM_BY_ROOM_ID', async roomId => {
	const { status, data, message } = await wrapFetchAuth(`room/${roomId}/orderItem`, {
		method: 'GET',
	});

	if (status !== 200) {
		throw openFailedAlert('generic', message);
	}

	const orderItemData = {};
	const list = data.map(item => {
		orderItemData[item.id] = keyTransform(item);

		return item.id;
	});

	return { data: orderItemData, list };
});

export const setAccessBooking = createAction('SET_ACCESS_BOOKING');



// WEBPACK FOOTER //
// ./src/actions/booking.js